import { useDebugValue, useState } from "react";
import userStore from "../zustand/userStore";
import { csrRegistrationThunk } from "../features/ticketing-app/redux/ticketingSlice";
import { useDispatch } from "react-redux";

/* ───────────────── Initial State ───────────────── */
const initialState = {
  selectedPlan: "",
  schoolName: "",
  address: "",
  principalName: "",
  principalMobile: "",
  classFrom: "",
  classTo: "",
  totalStudents: "",
  totalTeachers: "",
  board: "",
  schoolType: "",
  contactPerson: "",
  contactNumber: "",
  contactEmail: "",
};

/* ───────────────── Styles ───────────────── */
const styles = {
  page: {
    background:
      "linear-gradient(135deg, #e8f4fd 0%, #f0f7ff 50%, #e8f0fe 100%)",
    padding: "32px 16px",
    // fontFamily: "'Georgia', serif",
    // fontFamily: "'Georgia', serif",
  },
  card: {
    maxWidth: 820,
    margin: "0 auto",
    background: "#ffffff",
    borderRadius: 20,
    boxShadow: "0 8px 48px rgba(37,99,168,0.12)",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(135deg, #1a3a5c 0%, #2563a8 100%)",
    padding: "20px 28px",
    color: "#fff",
  },
  headerTitle: { margin: 0, fontSize: 22, fontWeight: 700 },
  headerSub: { fontSize: 13, opacity: 0.85 },
  formBody: { padding: "36px 40px" },
  sectionBlock: {
    borderLeft: "4px solid #2563a8",
    paddingLeft: 20,
    marginBottom: 36,
  },
  sectionTitle: {
    // fontSize: 11,
    fontSize: 14,
    fontWeight: 700,
    color: "#2563a8",
    // textTransform: "uppercase",
    marginBottom: 1,
  },
  label: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 5,
    display: "block",
  },
  star: { color: "red", marginLeft: 4 },
  row: { display: "flex", gap: 16 },
  col3: { flex: "1 1 25%" },
  input: {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 8,
    border: "1.5px solid #c8d8ea",
    fontSize: 14,
    marginBottom: 14,
  },
  inputFocus: {
    borderColor: "#2563a8",
    boxShadow: "0 0 0 3px rgba(37,99,168,0.1)",
  },
  select: {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 8,
    border: "1.5px solid #c8d8ea",
    fontSize: 14,
    marginBottom: 14,
  },
  btnPrimary: {
    background: "linear-gradient(90deg, #1a3a5c, #2563a8)",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "13px 40px",
    fontWeight: 700,
    cursor: "pointer",
    float: "right",
    marginBottom: "2%",
  },
};

/* ───────────────── Helpers ───────────────── */
const plans = ["25 Users", "50 Users", "100 Users", "100+ Users"];

const fieldLabels = {
  selectedPlan: "Selected Plan",
  schoolName: "School Name",
  address: "Address",
  principalName: "Principal Name",
  principalMobile: "Principal Mobile",
  classFrom: "Class From",
  classTo: "Class To",
  totalStudents: "Total Students",
  totalTeachers: "Total Teachers",
  board: "Board",
  schoolType: "School Type",
  contactPerson: "Contact Person",
  contactNumber: "Contact Number",
  contactEmail: "Contact Email",
};

const RequiredLabel = ({ children }) => (
  <label style={styles.label}>
    {children}
    <span style={styles.star}>*</span>
  </label>
);

/* ───────────────── Focus Inputs ───────────────── */
function FocusInput(props) {
  const [focus, setFocus] = useState(false);
  return (
    <input
      {...props}
      required
      style={{ ...styles.input, ...(focus ? styles.inputFocus : {}) }}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
    />
  );
}

function FocusSelect(props) {
  const [focus, setFocus] = useState(false);
  return (
    <select
      {...props}
      required
      style={{ ...styles.select, ...(focus ? styles.inputFocus : {}) }}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
    />
  );
}

/* ───────────────── Component ───────────────── */
export default function CSRSchoolPlanForm() {
  const [form, setForm] = useState(initialState);
  const userData = userStore((state) => state.user);
  const today = new Date().toISOString().split("T")[0];
  const [dateToShow, setDateToShow] = useState(today);
  const dispatch = useDispatch();
  const update = (key) => (e) =>
    setForm((p) => ({ ...p, [key]: e.target.value }));

  const validateForm = () => {
    const errors = [];

    // Mandatory check
    Object.entries(form).forEach(([key, value]) => {
      if (!value || value.toString().trim() === "") {
        errors.push(fieldLabels[key]);
      }
    });

    // Class validation
    const from = Number(form.classFrom);
    const to = Number(form.classTo);
    if (!isNaN(from) && !isNaN(to) && from >= to) {
      errors.push("Class From must be LESS than Class To");
    }

    // Phone validation
    if (form.contactNumber.length !== 10) {
      errors.push("Contact Number must be exactly 10 digits");
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.contactEmail)) {
      errors.push("Enter a valid Contact Email");
    }

    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (errors.length) {
      alert(
        "Please correct the following:\n\n" +
          errors.map((e, i) => `${i + 1}. ${e}`).join("\n"),
      );
      return;
    }

    console.log("CSR School Plan Request:", form);
    const {
      selectedPlan,
      schoolName,
      address,
      principalName,
      principalMobile,
      classFrom,
      classTo,
      totalStudents,
      totalTeachers,
      board,
      schoolType,
      contactPerson,
      contactNumber,
      contactEmail,
    } = form;
    const body = {
      school_user_id: userData?.admin_id,
      plan: selectedPlan,
      school_name: schoolName,
      address: address,
      principal_name: principalName,
      principal_mobile: principalMobile,
      class_from: classFrom,
      class_to: classTo,
      total_students: totalStudents,
      total_teachers: totalTeachers,
      board: board,
      school_type: schoolType,
      contact_person_name: contactPerson,
      contact_number: contactNumber,
      email: contactEmail,
    };
    console.log("finalData", JSON.stringify(body));
    const action = await dispatch(csrRegistrationThunk(body));
    console.log("action", JSON.stringify(action));
    // ✅ SUCCESS
    if (csrRegistrationThunk.fulfilled.match(action)) {
      const message =
        action.payload?.message || "CSR registration submitted successfully!";

      alert(message); // waits until OK clicked
      window.location.reload(); // reload after OK
    }

    // ❌ ERROR
    if (csrRegistrationThunk.rejected.match(action)) {
      const errorMsg =
        action.payload?.message || "Something went wrong. Please try again.";

      alert(errorMsg);
    }
    // alert("Form submitted successfully!");
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>
            Request for School Plan under CSR Scheme
          </h1>
          <p style={styles.headerSub}>
            We are interested in knowing more about the School Plan.
          </p>
        </div>

        <div style={styles.formBody}>
          <div style={styles.sectionBlock}>
            <p style={styles.sectionTitle}>Select a Plan</p>
            <RequiredLabel>Plan</RequiredLabel>
            <FocusSelect
              value={form.selectedPlan}
              onChange={update("selectedPlan")}
            >
              <option value="">Select</option>
              {plans.map((p) => (
                <option key={p}>{p}</option>
              ))}
            </FocusSelect>
          </div>

          <div style={styles.sectionBlock}>
            <p style={styles.sectionTitle}>School Details</p>

            <RequiredLabel>a. Name of School</RequiredLabel>
            <FocusInput
              value={form.schoolName}
              onChange={update("schoolName")}
            />

            <RequiredLabel>b. Address</RequiredLabel>
            <FocusInput value={form.address} onChange={update("address")} />

            <RequiredLabel>c. Name of the Principal / HOS</RequiredLabel>
            <FocusInput
              value={form.principalName}
              onChange={update("principalName")}
            />

            <RequiredLabel>d. Mobile No of the Principal/ HOS</RequiredLabel>
            <FocusInput
              type="tel"
              maxLength={10}
              value={form.principalMobile}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  principalMobile: e.target.value.replace(/\D/g, ""),
                }))
              }
            />

            <div style={styles.row}>
              <div style={styles.col3}>
                <RequiredLabel>e. Classes From</RequiredLabel>
                <FocusInput
                  type="number"
                  value={form.classFrom}
                  onChange={update("classFrom")}
                />
              </div>
              <div style={styles.col3}>
                <RequiredLabel> To</RequiredLabel>
                <FocusInput
                  type="number"
                  value={form.classTo}
                  onChange={update("classTo")}
                />
              </div>
            </div>

            <div style={styles.row}>
              <div style={styles.col3}>
                <RequiredLabel>f. Total Students</RequiredLabel>
                <FocusInput
                  value={form.totalStudents}
                  onChange={update("totalStudents")}
                />
              </div>

              <div style={styles.col3}>
                <RequiredLabel>g. Total Teachers</RequiredLabel>
                <FocusInput
                  value={form.totalTeachers}
                  onChange={update("totalTeachers")}
                />
              </div>
            </div>
            <div style={styles.row}>
              <div style={styles.col3}>
                <RequiredLabel>h. Board</RequiredLabel>
                <FocusInput value={form.board} onChange={update("board")} />
              </div>
              <div style={styles.col3}>
                <RequiredLabel>i. School Type</RequiredLabel>
                <FocusSelect
                  value={form.schoolType}
                  onChange={update("schoolType")}
                >
                  <option value="">Select</option>
                  <option>Private</option>
                  <option>Government</option>
                  <option>Government Aided</option>
                </FocusSelect>
              </div>
            </div>
            <RequiredLabel>j. Name of the Contact Person</RequiredLabel>
            <FocusInput
              value={form.contactPerson}
              onChange={update("contactPerson")}
            />
            <div style={styles.row}>
              <div style={styles.col3}>
                <RequiredLabel>k. Contact Number</RequiredLabel>
                <FocusInput
                  type="tel"
                  maxLength={10}
                  value={form.contactNumber}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      contactNumber: e.target.value.replace(/\D/g, ""),
                    }))
                  }
                />
              </div>
              <div style={styles.col3}>
                <RequiredLabel>Contact Email</RequiredLabel>
                <FocusInput
                  type="email"
                  value={form.contactEmail}
                  onChange={update("contactEmail")}
                />
              </div>
            </div>
            <label style={styles.label}>Date</label>
            <FocusInput
              type="date"
              value={dateToShow}
              onChange={(e) => setDateToShow(e.target.value)}
            />
          </div>

          <button style={styles.btnPrimary} onClick={handleSubmit}>
            Submit Request
          </button>
        </div>
      </div>
    </div>
  );
}
